import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import { Helmet } from 'react-helmet-async';

const PLATFORMS = ['Person Centred Software (PCS)', 'Camascope', 'Planday'];

const HEALTH_ALERTS = [
  'Problems with eating',
  'Signs of confusion',
  'Pain increase',
  'Falls risk',
  'Mental health risks',
  'Irregular cardiac activity',
  'Infection risk',
];

const VIMEO_CARE_RECORDS_ID = '836062476';

type FeatureIconProps = {
  children: ReactNode;
};

function FeatureIcon({ children }: FeatureIconProps) {
  return (
    <span className="dt-feature-card__icon" aria-hidden="true">
      {children}
    </span>
  );
}

function DigitalTransformation() {
  useEffect(() => {
    const scriptSrc = 'https://player.vimeo.com/api/player.js';
    if (document.querySelector(`script[src="${scriptSrc}"]`)) return;

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      <Helmet>
        <title>Digital Transformation | Graham Care</title>
        <meta
          name="description"
          content="How Graham Care integrates digital platforms, predictive analytics and real-time monitoring to deliver safer, more personalised care."
        />
      </Helmet>

      <div className="page digital-transformation-page">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="content-section dt-hero" aria-label="Digital Transformation">
            <div className="dt-hero__container">
              <div className="dt-hero__text">
                <h1>Technology in service of better care</h1>
                <p className="dt-hero__strapline">Personalisation through digitalisation</p>
                <p>
                  At Graham Care Group, we believe that technology, when thoughtfully implemented, has the power to
                  transform the experience of care — for residents, for families and for the teams who support them
                  every day. Our digital transformation programme is not about technology for its own sake. It is about
                  ensuring that the right information reaches the right people at the right time, so that every
                  decision made in our homes is informed, proactive and centred on the individual.
                </p>
              </div>
              <div className="dt-hero__image">
                <img
                  src="/Staff_talking.png"
                  alt="Graham Care team member using digital care technology"
                />
              </div>
            </div>
          </section>

          <section className="content-section dt-approach">
            <div className="dt-approach__container">
              <blockquote className="dt-statement">
                <p>
                  Deploying individual digital systems is only part of the picture. What sets our approach apart is the
                  way those systems work together. By integrating our platforms into a unified data environment, we are
                  able to move beyond reactive care and towards a model that anticipates need, monitors wellbeing in
                  real time and gives our managers the insight they need to maintain the highest standards — around the
                  clock.
                </p>
              </blockquote>
            </div>
          </section>

          <section className="content-section dt-platforms" aria-labelledby="dt-platforms-heading">
            <div className="dt-platforms__container">
              <p id="dt-platforms-heading" className="dt-platforms__label">
                Our core platforms
              </p>
              <ul className="dt-platforms__list">
                {PLATFORMS.map((platform) => (
                  <li key={platform} className="dt-platforms__chip">
                    {platform}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="content-section dt-features" aria-label="Digital transformation capabilities">
            <div className="dt-features__container">
              <ul className="dt-features__grid">
                <li className="dt-feature-card">
                  <FeatureIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <rect x="3" y="4" width="18" height="14" rx="2" />
                      <path d="M7 20h10M9 16v4M15 16v4" />
                      <path d="M7 10h4M7 13h7" />
                    </svg>
                  </FeatureIcon>
                  <h2>Real-time compliance management</h2>
                  <p>
                    Data streamed from across our digital systems feeds into bespoke compliance dashboards, giving home
                    managers and stakeholders a comprehensive, real-time view of care standards at a glance. This frees
                    managers from manual reporting and provides continuous, actionable insight — enabling them to
                    identify and address areas for improvement as they arise, rather than in retrospect. Our compliance
                    manager conducts in-person visits to each home at least once a week, combining quantitative data
                    with on-the-ground, qualitative expertise.
                  </p>
                </li>

                <li className="dt-feature-card">
                  <FeatureIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <path d="M12 3v3M12 18v3M4.2 7.2l2.1 2.1M17.7 16.8l2.1 2.1M3 12h3M18 12h3M4.2 16.8l2.1-2.1M17.7 7.2l2.1-2.1" />
                      <circle cx="12" cy="12" r="4" />
                    </svg>
                  </FeatureIcon>
                  <h2>Predictive health alerts</h2>
                  <p>
                    By combining data from multiple sources and applying artificial intelligence, we have developed a
                    predictive alert system that identifies early indicators of health deterioration before they
                    escalate. Alerts are surfaced directly to home managers and care teams, enabling timely, preventive
                    intervention and supporting better clinical outcomes.
                  </p>
                  <ul className="dt-feature-card__pills">
                    {HEALTH_ALERTS.map((alert) => (
                      <li key={alert}>{alert}</li>
                    ))}
                  </ul>
                </li>

                <li className="dt-feature-card">
                  <FeatureIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <path d="M21 12.5A9 9 0 1 1 12 3v9l6.5 3.5" />
                      <path d="M12 7v5" />
                    </svg>
                  </FeatureIcon>
                  <h2>Resident wellbeing monitoring</h2>
                  <p>
                    Using remote monitoring technology, our teams are able to observe resident wellbeing throughout the
                    night without the need for routine physical checks. Rather than disturbing residents at fixed
                    intervals, night staff monitor from a central dashboard and respond only when assistance is genuinely
                    needed — enabling residents, regardless of their care needs, to benefit from restful, uninterrupted
                    sleep. Each carer carries a personal handheld device, ensuring call bell alerts reach the right
                    person on the right floor instantly — with prompts to ensure every resident receives a response
                    within five minutes. Technology handles the logistics, so our people can focus on the person.
                  </p>
                </li>

                <li className="dt-feature-card">
                  <FeatureIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <rect x="3" y="4" width="18" height="17" rx="2" />
                      <path d="M8 2v4M16 2v4M3 10h18" />
                      <path d="M8 14h2M14 14h2M8 17h2" />
                    </svg>
                  </FeatureIcon>
                  <h2>Allocation management</h2>
                  <p>
                    Our allocation management system enables home managers to assign care shifts by floor, room or
                    resident group — giving carers a clear picture of their responsibilities at the start of every
                    shift. This continuity of assignment supports carers in building meaningful relationships with a
                    consistent group of residents, improving both the quality and the personalisation of care.
                  </p>
                </li>

                <li className="dt-feature-card dt-feature-card--video">
                  <FeatureIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <path d="M4 6h12v12H4z" />
                      <path d="M16 10l4-2v8l-4-2" />
                    </svg>
                  </FeatureIcon>
                  <h2>Real-time care record sharing</h2>
                  <div className="dt-feature-card__video">
                    <div className="dt-feature-card__video-inner">
                      <iframe
                        src={`https://player.vimeo.com/video/${VIMEO_CARE_RECORDS_ID}?badge=0&autopause=0&player_id=0&app_id=58479`}
                        title="Real-time care record sharing at Graham Care"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </li>

                <li className="dt-feature-card">
                  <FeatureIcon>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <circle cx="9" cy="8" r="3" />
                      <circle cx="17" cy="9" r="2.5" />
                      <path d="M3 20c0-3 3-5 6-5s6 2 6 5M14 20c0-2 2-3.5 4-3.5" />
                    </svg>
                  </FeatureIcon>
                  <h2>Staff support</h2>
                  <p>
                    Our teams are trained on all digital systems from day one, giving them the confidence to use
                    technology as a natural part of how they work. Every carer carries a personal handheld device, with
                    call bell alerts routed directly to them to ensure every resident receives a response within five
                    minutes. Nurses have access to a real-time monitoring dashboard to support swift responses to
                    unexpected changes. In a number of our homes, facial recognition terminals enable accurate monitoring
                    of care hours and a prompt, discreet response to any resident identified as wandering during the
                    night. Our carers are also empowered to administer medication through our eMAR system, with full
                    clinical oversight from our nursing team — giving our people more time, more confidence and more
                    information to deliver truly personalised care.
                  </p>
                </li>
              </ul>
            </div>
          </section>

          <section className="content-section dt-closing">
            <div className="dt-closing__container">
              <blockquote className="dt-statement">
                <p>
                  Digital transformation in care is an ongoing journey. We remain committed to evolving our systems and
                  practices in step with advances in technology — always with the goal of delivering better, safer and
                  more personalised outcomes for every resident in our homes.
                </p>
              </blockquote>
              <p className="dt-closing__cta">
                <Link to="/contact-us" className="nurse-care-link">
                  Contact us to find out more
                </Link>
              </p>
            </div>
          </section>
        </main>

        <footer className="footer">
          <section className="footer__enquiry">
            <div className="footer__enquiry-container">
              <Link to="/contact-us" className="footer__enquiry-link">
                Enquire about Graham Care <span className="footer__arrow">▶</span>
              </Link>
            </div>
          </section>
          <div className="footer__container">
            <section className="contact-section">
              <div className="contact-section__content">
                <p>Graham Care Ltd, KT15 1HH</p>
                <p>Copyright © Graham Care Ltd</p>
              </div>
            </section>
          </div>
        </footer>
      </div>
    </>
  );
}

export default DigitalTransformation;
